import React, { useMemo } from 'react';
import './TimelineChart.css';
import { useTranslation } from 'react-i18next';
import { formatDateForCountry, getCountryByCode, getLocaleByCountryCode, uses12HourFormat } from '../utils/countries';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost';

function TimelineChart({ checks, onCheckClick, user }) {
  const userCountry = user?.country;
  const countryInfo = userCountry ? getCountryByCode(userCountry) : null;
  const timezone = countryInfo?.timezone || 'UTC';
  const locale = getLocaleByCountryCode(userCountry);

  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language || 'en';

  // Saat bazlı gruplama - KULLANICININ TIMEZONE'INA GÖRE
  const { hourlyChecks, hourTimestamps } = useMemo(() => {
    const hours = {};
    const timestamps = {};
    const use12Hour = uses12HourFormat(userCountry);

    checks.forEach(check => {
      const date = new Date(check.checked_at);
      
      // Timezone'a göre formatlama
      const formatter = new Intl.DateTimeFormat(locale, {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        hour12: use12Hour // Dinamik AM/PM
      });
      
      const parts = formatter.formatToParts(date);
      const year = parts.find(p => p.type === 'year')?.value;
      const month = parts.find(p => p.type === 'month')?.value;
      const day = parts.find(p => p.type === 'day')?.value;
      const hour = parts.find(p => p.type === 'hour')?.value;
      const dayPeriod = parts.find(p => p.type === 'dayPeriod')?.value; // AM/PM
      
      // Tarih formatı ülkeye göre
      let dateStr;
      if (userCountry === 'TR') {
        dateStr = `${day}.${month}.${year}`;
      } else if (['US', 'CA', 'PH'].includes(userCountry)) {
        dateStr = `${month}/${day}/${year}`;
      } else {
        dateStr = `${day}/${month}/${year}`;
      }
      
      // Saat formatı (AM/PM varsa ekle)
      const hourStr = use12Hour && dayPeriod 
        ? `${hour}:00 ${dayPeriod}` 
        : `${hour}:00`;
      
      const key = `${dateStr} - ${hourStr}`;

      if (!hours[key]) {
        hours[key] = [];
        // Bu saatin başlangıç timestamp'ini kaydet (sıralama için)
        timestamps[key] = date.getTime();
      }

      hours[key].push(check);
    });

    return { hourlyChecks: hours, hourTimestamps: timestamps };
  }, [checks, userCountry, timezone, locale]);

  // Saatleri timestamp bazlı sırala (doğru sıralama için)
  const sortedHours = useMemo(() => {
    return Object.keys(hourlyChecks).sort((a, b) => {
      return hourTimestamps[b] - hourTimestamps[a];
    });
  }, [hourlyChecks, hourTimestamps]);

  // ✅ Tooltip oluşturma - KULLANICININ TIMEZONE'INA GÖRE
  const getTooltip = (check) => {
    // Timezone'a göre saat formatlama

    const time = formatDateForCountry(check.checked_at, userCountry, {}, currentLanguage);
    
    if (check.status === 'success') {
      if (check.error_message?.startsWith('CF_PROTECTED')) {
        return `${time} - ⚠️ Cloudflare koruması aktif (site çalışıyor)`;
      }
      return `${time} - ✓ ${check.status_code} (${check.response_time}ms)`;
    }
    
    // Hata durumunda katman bilgileri
    let tooltip = `${time} - ✗ Hata\n`;
    
    if (check.dns_status) tooltip += `DNS: ${check.dns_status === 'ok' ? '✅' : '❌'}\n`;
    if (check.tcp_status) tooltip += `TCP: ${check.tcp_status === 'ok' ? '✅' : '❌'}\n`;
    if (check.ssl_status) tooltip += `SSL: ${check.ssl_status === 'ok' ? '✅' : '❌'}\n`;
    if (check.http_status) tooltip += `HTTP: ${check.http_status === 'ok' ? '✅' : '❌'}\n`;
    if (check.status_code) tooltip += `Code: ${check.status_code}\n`;
    if (check.error_message) tooltip += `${check.error_message}`;
    
    return tooltip.trim();
  };

  return (
    <div className="timeline-container">
      <div className="timeline-scroll">
        {sortedHours.map(hour => (
          <div key={hour} className="timeline-hour">
            
            <div className="timeline-hour-label">
              <span className="line_span"></span>{hour}<span className="line_span"></span>
            </div>

            <div className="timeline-checks">
              {hourlyChecks[hour].map((check) => (
                <div
                  key={check.id}
                  className={`timeline-dot 
                    ${check.error_message?.startsWith('CF_PROTECTED') ? 'cf-protected' : check.status} 
                    ${check.screenshot_path ? 'has-screenshot' : ''}
                    ${check.dns_status === 'fail' || check.tcp_status === 'fail' || check.ssl_status === 'fail' ? 'layer-error' : ''}
                  `}
                  onClick={() => onCheckClick(check)}
                  title={getTooltip(check)}
                />
              ))}
            </div>

          </div>
        ))}
      </div>

      {checks.length === 0 && (
        <div className="timeline-empty">
          Henüz kontrol verisi yok
        </div>
      )}
    </div>
  );
}

export default TimelineChart;
